import useTranslation from 'next-translate/useTranslation';
import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { toast } from '@/components/Toaster/Toaster';
import useAuth from '@/hooks/useAuth';
import { userApi } from '@/services';

function modfiyTask(task, mode, payload) {
  if (['check', 'uncheck'].includes(mode)) {
    return { ...task, done: mode === 'check' };
  }
  if (mode === 'rename') {
    return { ...task, name: payload };
  }
  if (mode === 'renameProject') {
    return {};
  }
  if (mode === 'renameDescription') {
    return { ...task, description: payload };
  }
  if (mode === 'renameNotes') {
    return { ...task, notes: payload };
  }
  if (mode === 'sortTask') {
    return { ...task, sortposition: payload };
  }

  return task;
}

const useMutationProject = (projectId, taskId, sid, onError) => {
  const { t: h } = useTranslation('common-toast');
  const toaster = h('toast', {}, { returnObjects: true });
  const { initialized, authenticated } = useAuth();
  const queryClient = useQueryClient();

  const modfiyProject = useCallback(
    (payload) => {
      const currentProject = queryClient.getQueryData(['project', projectId]);
      return { ...currentProject, name: payload };
    },
    [projectId, queryClient]
  );

  const createNextTasks = useCallback(
    (mode, payload) => {
      const currentProject = queryClient.getQueryData(['project', projectId]);

      if (mode === 'delete') {
        if (sid) {
          return currentProject?.task?.map((task) => {
            return { ...task, subtask: task?.subtask?.filter((subTask) => subTask.id !== sid) };
          });
        }

        return currentProject.task.filter((task) => task.id !== taskId);
      }

      if (mode === 'addTask') {
        if (taskId) {
          return currentProject?.task?.map((task) => {
            if (task.id === taskId) {
              return {
                ...task,
                subtask: [
                  ...task.subtask,
                  {
                    id: '0',
                    id_account: currentProject.id_account,
                    id_task: taskId,
                    name: payload.name,
                    description: payload.description,
                    creationtime: '',
                    updatetime: '',
                    done: false,
                    deleted: false,
                    link: null,
                    notes: null,
                    sortposition: -1000000,
                  },
                ],
              };
            }
            return task;
          });
        }

        return [
          ...currentProject.task,
          {
            id: '0',
            id_account: currentProject.id_account,
            id_project: projectId,
            placeholder: payload.name,
            name: '',
            description: payload.description,
            creationtime: '',
            updatetime: '',
            done: false,
            deleted: false,
            sortposition: -1000000,
            subtask: [],
            progress: {
              cnt_info: '',
              done_percent: 0,
              cnt_tasks_all: 0,
              cnt_tasks_done: 0,
              cnt_tasks_undone: 0,
            },
          },
        ].sort((a, b) => a.sortposition - b.sortposition);
      }

      return currentProject.task.map((task) => {
        if (task.id === taskId) {
          if (sid) {
            const nextSubTasks = task.subtask.map((subTask) => {
              if (subTask.id === sid) {
                return modfiyTask(subTask, mode, payload);
              }
              return subTask;
            });

            return { ...task, subtask: nextSubTasks };
          }

          return modfiyTask(task, mode, payload);
        }

        return task;
      });
    },
    [projectId, queryClient, sid, taskId]
  );

  const commonQueryConfig = useCallback(
    (mode) => {
      return {
        onMutate: async (newValue) => {
          await queryClient.cancelQueries(['project']);

          const previousProjects = queryClient.getQueryData(['project']);
          const previousProject = queryClient.getQueryData(['project', projectId]);

          if (mode === 'renameProject') {
            queryClient.setQueryData(['project', projectId], modfiyProject(newValue));
            queryClient.setQueryData(['project'], (old) =>
              old?.map((project) => {
                if (project.id === projectId) {
                  return modfiyProject(newValue);
                }
                return project;
              })
            );
          }

          queryClient.setQueryData(['project', projectId], (old) => ({
            ...old,
            task: createNextTasks(mode, newValue),
          }));

          queryClient.setQueryData(['project'], (old) =>
            old?.map((project) => {
              if (project.id === projectId) {
                return {
                  ...project,
                  task: createNextTasks(mode, newValue),
                };
              }
              return project;
            })
          );

          return { previousProjects, previousProject };
        },
        onError: (err, newTodo, context) => {
          queryClient.setQueryData(['project'], context?.previousProjects);
          queryClient.setQueryData(['project', projectId], context?.previousProject);
        },
        onSettled: async () => {
          await queryClient.invalidateQueries(['project']);
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries('getAppointment');
          if (mode === 'check') {
            toast({
              type: 'success',
              message: toaster.projects.check,
              dismissable: true,
              withIcon: true,
            });
          }
          if (mode === 'uncheck') {
            toast({
              type: 'success',
              message: toaster.projects.uncheck,
              dismissable: true,
              withIcon: true,
            });
          }
          if (mode === 'delete') {
            toast({
              type: 'success',
              message: toaster.projects.delete,
              dismissable: true,
              withIcon: true,
            });
          }
        },
      };
    },
    [
      createNextTasks,
      modfiyProject,
      projectId,
      queryClient,
      toaster.projects.check,
      toaster.projects.delete,
      toaster.projects.uncheck,
    ]
  );

  const fetcher = useCallback(
    async (data) => {
      const url = !sid
        ? `/projects/${projectId}/task/${taskId}`
        : `/projects/${projectId}/task/${taskId}/subtask/${sid}`;

      return userApi.put(url, data);
    },
    [projectId, sid, taskId]
  );

  const generalHandler = useCallback(
    async (mode, payload) => {
      if (initialized && !authenticated) {
        onError?.();
        return undefined;
      }

      const nextTasks = createNextTasks(mode, payload);
      const nextSingleTask = nextTasks.find((task) => task.id === taskId);

      if (sid) {
        const nextSingleSubTask = nextSingleTask.subtask.find((task) => task.id === sid);
        return fetcher(nextSingleSubTask);
      }

      return fetcher(nextSingleTask);
    },
    [authenticated, createNextTasks, fetcher, initialized, onError, sid, taskId]
  );

  const deleteHandler = useCallback(async () => {
    if (initialized && !authenticated) {
      return () => onError?.();
    }

    const url = !sid ? `/projects/${projectId}/task/${taskId}` : `/projects/${projectId}/task/${taskId}/subtask/${sid}`;
    const { data } = await userApi.delete(url);

    return data;
  }, [authenticated, initialized, onError, projectId, sid, taskId]);

  const addHandler = useCallback(
    async (params) => {
      if (initialized && !authenticated) {
        return () => onError?.();
      }

      const url = !taskId ? `/projects/${projectId}/task` : `/projects/${projectId}/task/${taskId}/subtask`;
      const { data } = await userApi.post(url, params);

      return data;
    },
    [authenticated, initialized, onError, projectId, taskId]
  );

  const renameProjectHandler = useCallback(
    (payload) => {
      const newProject = modfiyProject(payload);
      return userApi.put(`/projects/${projectId}`, newProject);
    },

    [modfiyProject, projectId]
  );

  const { mutateAsync: check } = useMutation(async () => generalHandler('check'), commonQueryConfig('check'));
  const { mutateAsync: uncheck } = useMutation(async () => generalHandler('uncheck'), commonQueryConfig('uncheck'));
  const { mutateAsync: rename } = useMutation(
    async (newValue) => generalHandler('rename', newValue),
    commonQueryConfig('rename')
  );
  const { mutateAsync: renameDescription } = useMutation(
    async (newValue) => generalHandler('renameDescription', newValue),
    commonQueryConfig('renameDescription')
  );
  const { mutateAsync: renameNotes } = useMutation(
    async (newValue) => generalHandler('renameNotes', newValue),
    commonQueryConfig('renameNotes')
  );
  const { mutateAsync: renameProject } = useMutation(
    async (newValue) => renameProjectHandler(newValue),
    commonQueryConfig('renameProject')
  );
  const { mutateAsync: deleteTask } = useMutation(deleteHandler, commonQueryConfig('delete'));
  const { mutateAsync: addTask } = useMutation(addHandler, commonQueryConfig('addTask'));

  return {
    check,
    uncheck,
    rename,
    renameDescription,
    renameNotes,
    renameProject,
    deleteTask,
    addTask,
  };
};

export default useMutationProject;
